import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  MessageSquare, Megaphone, LifeBuoy, Bell, Video, Phone, Hash,
  Settings as SettingsIcon, ArrowRight, Plus, FolderOpen, FileText,
  AlertCircle, CheckCircle2, MoreHorizontal, AtSign, FileSpreadsheet,
  FileImage, Mail, Users,
} from "lucide-react";

/* ============== KPI sparkline card ============== */
function CommKpi({ icon: Icon, label, value, trend, trendDir, tone, sparkColor, testid }) {
  const tones = {
    violet: "from-violet-100 to-violet-50 text-violet-600",
    rose:   "from-rose-100 to-rose-50 text-rose-600",
    blue:   "from-blue-100 to-blue-50 text-blue-600",
    fuchsia:"from-fuchsia-100 to-fuchsia-50 text-fuchsia-600",
    emerald:"from-emerald-100 to-emerald-50 text-emerald-600",
  };
  const spark = [4, 6, 5, 8, 6, 9, 7, 11];
  return (
    <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${tones[tone]} flex items-center justify-center ring-1 ring-slate-100`}>
              <Icon className="w-4.5 h-4.5" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">{label}</div>
              <div className="text-3xl font-bold font-[Outfit] tracking-tight text-slate-900 tabular-nums leading-tight mt-0.5">{value}</div>
            </div>
          </div>
          <div className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold ${trendDir === "up" ? "text-emerald-600" : "text-rose-600"}`}>
            {trendDir === "up" ? "↑" : "↓"} {trend} vs yesterday
          </div>
        </div>
      </div>
      {/* Mini sparkline */}
      <svg viewBox="0 0 120 26" className="w-full h-7 mt-3">
        <polyline
          points={spark.map((v, i) => `${(i * 120) / (spark.length - 1)},${26 - (v / 12) * 22 - 1}`).join(" ")}
          fill="none" stroke={sparkColor} strokeWidth="1.5"
        />
      </svg>
    </Card>
  );
}

/* ============== Quick action tile ============== */
function QuickActionTile({ icon: Icon, label, sub, onClick, tone, testid }) {
  const tones = {
    violet:  "from-violet-100 to-violet-50 text-violet-600",
    fuchsia: "from-fuchsia-100 to-fuchsia-50 text-fuchsia-600",
    rose:    "from-rose-100 to-rose-50 text-rose-600",
    emerald: "from-emerald-100 to-emerald-50 text-emerald-600",
  };
  return (
    <button
      onClick={onClick}
      className="flex-1 flex items-center gap-3 p-4 rounded-2xl bg-white border border-slate-200 hover:border-violet-300 hover:shadow-md transition-all text-left group"
      data-testid={testid}
    >
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tones[tone]} flex items-center justify-center shrink-0 ring-1 ring-slate-100 group-hover:scale-105 transition-transform`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-slate-900 truncate">{label}</div>
        <div className="text-[11px] text-slate-500 truncate mt-0.5">{sub}</div>
      </div>
    </button>
  );
}

/* ============== Generic card header ============== */
function CardHeader({ icon: Icon, label, sub, badge, badgeTone, testid }) {
  const tones = { live: "bg-emerald-100 text-emerald-700", beta: "bg-amber-100 text-amber-700", new: "bg-violet-100 text-violet-700" };
  return (
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-100 to-fuchsia-50 text-violet-600 flex items-center justify-center ring-1 ring-violet-100 shrink-0">
          <Icon className="w-4.5 h-4.5" />
        </div>
        <div className="min-w-0">
          <div className="text-base font-bold font-[Outfit] text-slate-900 truncate">{label}</div>
          <div className="text-[11px] text-slate-500 truncate">{sub}</div>
        </div>
      </div>
      {badge && <Badge className={`${tones[badgeTone] || tones.live} border-0 text-[10px] font-bold uppercase tracking-wider`} data-testid={testid}>{badge}</Badge>}
    </div>
  );
}

/* ============== Main component ============== */
export default function CommunicationSuite() {
  const nav = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [annc, setAnnc] = useState([]);

  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data || [])).catch(() => setEmployees([]));
    api.get("/announcements").then(({ data }) => setAnnc((data || []).slice(0, 3))).catch(() => setAnnc([]));
  }, []);

  const empByName = (name) => employees.find(e => (e.full_name || "").toLowerCase().includes(String(name).toLowerCase()));
  const initials = (n = "U") => n.split(" ").filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join("");

  /* ===== mock content (frontend-only until backend ready) ===== */
  const recentChats = [
    { id: 1, name: "Product Team",  last: "Riya: Shared the latest mockups...", time: "09:30 AM", unread: 4, who: "Riya Malhotra" },
    { id: 2, name: "Design Squad",  last: "Vikram: Let's sync after standup.",  time: "09:18 AM", unread: 2, who: "Vikram Singh" },
    { id: 3, name: "Arjun Mehta",   last: "You: Sounds good, thanks!",          time: "08:45 AM", unread: 0, who: "Arjun Mehta" },
  ];
  const tickets = [
    { id: "INC-1234", title: "Laptop not connecting to Wi-Fi", status: "Open",        tone: "amber" },
    { id: "PAY-5678", title: "Payroll clarification for May",   status: "In Progress", tone: "blue" },
    { id: "ACC-7890", title: "Access request for Figma",        status: "Resolved",    tone: "emerald" },
  ];
  const notifications = [
    { id: 1, title: "You were mentioned in #design",  count: 3, ago: "2m ago",  icon: AtSign,        tone: "rose" },
    { id: 2, title: "Leave request needs approval",   count: 2, ago: "15m ago", icon: AlertCircle,   tone: "amber" },
    { id: 3, title: "New reply on ticket #INC-1234",  count: 1, ago: "32m ago", icon: MessageSquare, tone: "blue" },
  ];
  const sharedFiles = [
    { name: "Q2_Roadmap_(Final).pdf",   icon: FileText,        color: "text-rose-600 bg-rose-50",       channel: "#product-team", ago: "2h ago" },
    { name: "Budget_Review_May.xlsx",   icon: FileSpreadsheet, color: "text-emerald-600 bg-emerald-50", channel: "#finance",       ago: "5h ago" },
    { name: "Design_System_v2.fig",     icon: FileImage,       color: "text-violet-600 bg-violet-50",   channel: "#Design Squad",  ago: "1d ago" },
  ];
  const supportQueue = [
    { id: "INC-1240", title: "VPN connection issue", cat: "Infrastructure", status: "Open",        tone: "rose",    ago: "10m ago" },
    { id: "ACC-1241", title: "Request for app access", cat: "Access",       status: "In Progress", tone: "blue",    ago: "25m ago" },
    { id: "PAY-5679", title: "Payroll discrepancy", cat: "Payroll",         status: "Open",        tone: "amber",   ago: "45m ago" },
    { id: "INC-1242", title: "Email not syncing", cat: "Email",             status: "Resolved",    tone: "emerald", ago: "1h ago"  },
  ];
  const callParticipants = ["Arjun Mehta","Tanvi Joshi","Riya Malhotra","Vikram Singh"];

  const statusBadge = (status, tone) => {
    const tones = {
      amber:   "bg-amber-100 text-amber-700",
      blue:    "bg-blue-100 text-blue-700",
      emerald: "bg-emerald-100 text-emerald-700",
      rose:    "bg-rose-100 text-rose-700",
    };
    return <Badge className={`border-0 text-[10px] font-bold uppercase tracking-wider ${tones[tone]}`}>{status}</Badge>;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="communication-suite-page">
      {/* HEADER */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500">Work & Collab</div>
          <h1 className="text-3xl font-bold font-[Outfit] tracking-tight text-slate-900 mt-1">Communication Suite</h1>
          <p className="text-sm text-slate-600 mt-1.5">Every conversation, announcement, call, and ticket — unified in one place.</p>
        </div>
        <Button variant="outline" className="border-slate-200" data-testid="cs-customize"><SettingsIcon className="w-3.5 h-3.5 mr-1.5" />Customize</Button>
      </div>

      {/* KPI ROW */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <CommKpi icon={MessageSquare} label="Active Conversations" value="48" trend="18%" trendDir="up"   tone="violet"  sparkColor="#10b981" testid="cs-kpi-conversations" />
        <CommKpi icon={LifeBuoy}      label="Open Tickets"          value="16" trend="6%"  trendDir="down" tone="rose"    sparkColor="#ef4444" testid="cs-kpi-tickets" />
        <CommKpi icon={Megaphone}     label="Announcements Sent"    value="7"  trend="40%" trendDir="up"   tone="blue"    sparkColor="#3b82f6" testid="cs-kpi-announcements" />
        <CommKpi icon={AtSign}        label="Unread Mentions"       value="23" trend="5"   trendDir="up"   tone="fuchsia" sparkColor="#a855f7" testid="cs-kpi-mentions" />
        <CommKpi icon={Phone}         label="Calls Today"           value="12" trend="3"   trendDir="up"   tone="emerald" sparkColor="#10b981" testid="cs-kpi-calls" />
      </div>

      {/* QUICK ACTIONS */}
      <div className="flex items-center gap-3 flex-wrap">
        <QuickActionTile icon={MessageSquare} label="Start Chat"        sub="Message a team or colleague"      onClick={() => nav("/messaging")}     tone="violet"  testid="cs-qa-chat" />
        <QuickActionTile icon={Megaphone}     label="New Announcement"  sub="Post an update to everyone"        onClick={() => nav("/announcements")} tone="fuchsia" testid="cs-qa-announce" />
        <QuickActionTile icon={LifeBuoy}      label="Create Ticket"     sub="Get help from support"            onClick={() => nav("/helpdesk")}      tone="rose"    testid="cs-qa-ticket" />
        <QuickActionTile icon={Video}         label="Start Call"        sub="Voice or video call"              onClick={() => nav("/meetings-video")} tone="emerald" testid="cs-qa-call" />
        <button className="px-4 h-[72px] rounded-2xl bg-white border border-slate-200 hover:border-violet-300 hover:shadow-md transition flex items-center gap-2 text-slate-600 font-semibold text-sm" data-testid="cs-qa-more">
          <MoreHorizontal className="w-4 h-4" /> More Actions
        </button>
      </div>

      {/* MAIN GRID — 4 widget cards */}
      <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Direct & Group Chat */}
        <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="cs-chat-card">
          <CardHeader icon={MessageSquare} label="Direct & Group Chat" sub="1:1, team channels, threads, mentions" badge="Live" badgeTone="live" />
          <div className="space-y-3">
            {recentChats.map((c) => {
              const emp = empByName(c.who);
              return (
                <div key={c.id} className="flex items-center gap-2.5">
                  <Avatar className="w-9 h-9 ring-2 ring-white shadow-sm">
                    {emp?.avatar_url && <AvatarImage src={emp.avatar_url} alt={c.who} />}
                    <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold">{initials(c.who)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-semibold text-slate-900 truncate">{c.name}</span>
                      <span className="text-[10px] text-slate-500 shrink-0 ml-2">{c.time}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{c.last}</div>
                  </div>
                  {c.unread > 0 && <span className="w-5 h-5 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{c.unread}</span>}
                </div>
              );
            })}
          </div>
          <Link to="/messaging" className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-violet-700 hover:underline" data-testid="cs-chat-open">Open Chat <ArrowRight className="w-3 h-3" /></Link>
        </Card>

        {/* Announcements */}
        <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="cs-announcements-card">
          <CardHeader icon={Megaphone} label="Announcements" sub="Org-wide broadcasts & important updates" badge="Live" badgeTone="live" />
          <div className="space-y-3">
            {[
              { icon: Megaphone, title: "New Hybrid Work Policy", time: "Today, 09:15 AM", tone: "rose",    badge: "New" },
              { icon: Video,     title: "Q2 Townhall Recording",  time: "Yesterday, 05:20 PM", tone: "blue",  badge: null },
              { icon: Megaphone, title: "Wellness Program Launch", time: "May 24, 11:00 AM", tone: "violet", badge: null },
            ].map((a, i) => {
              const t = { rose: "text-rose-600 bg-rose-50", blue: "text-blue-600 bg-blue-50", violet: "text-violet-600 bg-violet-50" }[a.tone];
              return (
                <div key={i} className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-lg ${t} flex items-center justify-center shrink-0`}><a.icon className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[13px] font-semibold text-slate-900 truncate">{a.title}</span>
                      {a.badge && <Badge className="bg-violet-100 text-violet-700 border-0 text-[9px] font-bold">{a.badge}</Badge>}
                    </div>
                    <div className="text-[11px] text-slate-500">{a.time}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <Link to="/announcements" className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-violet-700 hover:underline" data-testid="cs-announcements-view">View All Announcements <ArrowRight className="w-3 h-3" /></Link>
        </Card>

        {/* Helpdesk & Tickets */}
        <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="cs-tickets-card">
          <CardHeader icon={LifeBuoy} label="Helpdesk & Tickets" sub="IT/HR support tickets & request status" badge="Live" badgeTone="live" />
          <div className="space-y-3">
            {tickets.map((t) => (
              <div key={t.id} className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center shrink-0"><AlertCircle className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900 truncate">{t.title}</div>
                  <div className="text-[11px] text-slate-500 font-mono">#{t.id}</div>
                </div>
                {statusBadge(t.status, t.tone)}
              </div>
            ))}
          </div>
          <Link to="/helpdesk" className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-violet-700 hover:underline" data-testid="cs-tickets-view">View All Tickets <ArrowRight className="w-3 h-3" /></Link>
        </Card>

        {/* Notification Center */}
        <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="cs-notifications-card">
          <CardHeader icon={Bell} label="Notification Center" sub="Mentions, alerts & system notifications" badge="Live" badgeTone="live" />
          <div className="space-y-3">
            {notifications.map((n) => {
              const t = { rose: "text-rose-600 bg-rose-50", amber: "text-amber-600 bg-amber-50", blue: "text-blue-600 bg-blue-50" }[n.tone];
              return (
                <div key={n.id} className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-lg ${t} flex items-center justify-center shrink-0`}><n.icon className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-slate-900 truncate">{n.title}</div>
                    <div className="text-[11px] text-slate-500">{n.ago}</div>
                  </div>
                  <span className="w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{n.count}</span>
                </div>
              );
            })}
          </div>
          <Link to="/inbox" className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-violet-700 hover:underline" data-testid="cs-notifications-view">View All Notifications <ArrowRight className="w-3 h-3" /></Link>
        </Card>
      </div>

      {/* SECONDARY ROW — Calls + Files */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Voice & Video Calls */}
        <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="cs-calls-card">
          <CardHeader icon={Video} label="Voice & Video Calls" sub="Click-to-call within Qrew HR" badge="Beta" badgeTone="beta" />
          <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-50/30 ring-1 ring-emerald-100 p-4 flex items-center gap-3" data-testid="cs-ongoing-call">
            <div className="flex items-center gap-2">
              <span className="relative flex w-2 h-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="text-[11px] uppercase tracking-wider font-bold text-emerald-700">Ongoing Call</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-slate-900 truncate">Product Standup</div>
              <div className="text-[11px] text-emerald-700 font-bold">14:36</div>
            </div>
            <div className="flex -space-x-2">
              {callParticipants.slice(0, 3).map((n) => {
                const emp = empByName(n);
                return (
                  <Avatar key={n} className="w-7 h-7 ring-2 ring-white">
                    {emp?.avatar_url && <AvatarImage src={emp.avatar_url} alt={n} />}
                    <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[9px] font-bold">{initials(n)}</AvatarFallback>
                  </Avatar>
                );
              })}
              <span className="w-7 h-7 rounded-full bg-slate-100 ring-2 ring-white text-[10px] font-bold text-slate-600 flex items-center justify-center">+5</span>
            </div>
          </div>
          <Link to="/meetings-video" className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-violet-700 hover:underline" data-testid="cs-calls-history">Call History <ArrowRight className="w-3 h-3" /></Link>
        </Card>

        {/* Shared Files */}
        <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="cs-files-card">
          <CardHeader icon={FolderOpen} label="Shared Files" sub="Recent files shared in conversations" badge="Live" badgeTone="live" />
          <div className="space-y-3">
            {sharedFiles.map((f, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-lg ${f.color} flex items-center justify-center shrink-0`}><f.icon className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900 truncate">{f.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">Shared in {f.channel}</div>
                </div>
                <span className="text-[10px] text-slate-500 shrink-0">{f.ago}</span>
              </div>
            ))}
          </div>
          <Link to="/documents" className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-violet-700 hover:underline" data-testid="cs-files-view">View All Files <ArrowRight className="w-3 h-3" /></Link>
        </Card>
      </div>

      {/* BOTTOM ROW — recent conversations / company announcements / support queue */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="cs-recent-conv">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold font-[Outfit] text-slate-900">Recent Conversations</h3>
            <button className="text-[11px] font-semibold text-violet-700 hover:underline">View All</button>
          </div>
          <div className="space-y-3">
            {[
              { name: "# Product Team", who: "Riya Malhotra", text: "Riya: Finalized the sprint plan. Please review.", time: "09:30 AM", unread: 4 },
              { name: "Design Squad",   who: "Vikram Singh", text: "Vikram: Pushed the latest changes to Figma.",   time: "09:18 AM", unread: 2 },
              { name: "Arjun Mehta (You)", who: "Arjun Mehta", text: "You: Let's align on the priorities today.",   time: "08:45 AM", unread: 0 },
              { name: "HR Team",        who: "Priya Sharma",   text: "Neha: Don't forget the policy update.",       time: "08:20 AM", unread: 1 },
              { name: "Support Team",   who: "Karan Joshi",    text: "Karan: Ticket #INC-1234 has been resolved.",  time: "Yesterday", unread: 0, resolved: true },
            ].map((c, i) => {
              const emp = empByName(c.who);
              return (
                <div key={i} className="flex items-center gap-2.5">
                  <Avatar className="w-9 h-9 ring-2 ring-white shadow-sm">
                    {emp?.avatar_url && <AvatarImage src={emp.avatar_url} alt={c.who} />}
                    <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold">{initials(c.who)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-semibold text-slate-900 truncate">{c.name}</span>
                      <span className="text-[10px] text-slate-500 shrink-0">{c.time}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{c.text}</div>
                  </div>
                  {c.unread > 0 ? <span className="w-5 h-5 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{c.unread}</span>
                                : c.resolved ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : null}
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="cs-company-announcements">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold font-[Outfit] text-slate-900">Company Announcements</h3>
            <button className="text-[11px] font-semibold text-violet-700 hover:underline">View All</button>
          </div>
          <div className="space-y-4">
            {[
              { icon: Megaphone, tone: "rose",   title: "New Hybrid Work Policy", body: "We're excited to introduce our new hybrid work policy…", time: "Today, 09:15 AM", badge: "New" },
              { icon: Video,     tone: "blue",   title: "Q2 Townhall Recording",   body: "The recording from our Q2 Townhall is now available.", time: "Yesterday, 05:20 PM" },
              { icon: Megaphone, tone: "violet", title: "Wellness Program Launch", body: "Join our new wellness program starting June 1.",        time: "May 24, 11:00 AM" },
            ].map((a, i) => {
              const t = { rose: "text-rose-600 bg-rose-50", blue: "text-blue-600 bg-blue-50", violet: "text-violet-600 bg-violet-50" }[a.tone];
              return (
                <div key={i} className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg ${t} flex items-center justify-center shrink-0`}><a.icon className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[13px] font-semibold text-slate-900">{a.title}</span>
                      {a.badge && <Badge className="bg-violet-100 text-violet-700 border-0 text-[9px] font-bold">{a.badge}</Badge>}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{a.body}</div>
                    <div className="text-[10px] text-slate-400 mt-1">{a.time}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="cs-support-queue">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold font-[Outfit] text-slate-900">Support Queue</h3>
            <button className="text-[11px] font-semibold text-violet-700 hover:underline">View All</button>
          </div>
          <div className="space-y-3">
            {supportQueue.map((q) => (
              <div key={q.id} className="flex items-center gap-2.5">
                <div className={`w-2 h-8 rounded-full ${
                  q.tone === "rose" ? "bg-rose-500" :
                  q.tone === "blue" ? "bg-blue-500" :
                  q.tone === "amber" ? "bg-amber-500" :
                                       "bg-emerald-500"
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold text-slate-900 truncate">{q.title}</div>
                  <div className="text-[10px] text-slate-500 font-mono">#{q.id} · {q.cat}</div>
                </div>
                <div className="text-right shrink-0 space-y-0.5">
                  {statusBadge(q.status, q.tone)}
                  <div className="text-[10px] text-slate-500">{q.ago}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
